/**
 * Подписка Pro: срок, карта, автопродление, остатки, оплаты, возврат
 * (макет docs/design/pro-2026-10-21, «Управление подпиской»).
 *
 * Всё в одном листе, без писем в поддержку: выключить продление и вернуть
 * деньги человек может сам. Подтверждения — без уговоров: что останется и до
 * какого числа.
 */
import { useEffect, useState } from 'react';
import { SheetWrap } from './sheet.js';
import { Icon } from './icon.js';
import { api, unwrap } from './sync.js';
import { accessChanged, dayLabel, useAccess, type AccessState } from './access.js';
import { ProLockup } from './pro-screen.js';

export interface Billing {
  billing: boolean;
  subscription: null | {
    tier: string; paid: boolean; endsAt: string | null; renews: boolean; method: string | null; cardSaved: boolean;
    months: number | null; nextAmount: number | null; graceUntil: string | null; failedAttempts: number;
  };
  payments: {
    id: string; kind: string; status: string; months: number; amount: number; early: boolean; method: string | null;
    at: string; until: string | null; refunded: number | null; refundStatus: string | null;
  }[];
  refund: null | {
    amount: number; paid: number; months: number; paidAt: string; periodStart: string; periodEnd: string; method: string | null;
  };
}

export const fetchBilling = async (): Promise<Billing> => await unwrap(await api('/billing')) as Billing;

/** Начать оплату: сервер считает цену и отдаёт страницу ЮKassa. */
export async function startCheckout(months: 1 | 3 | 6 | 12, renew: boolean): Promise<string> {
  const out = await unwrap(await api('/billing/checkout', { method: 'POST', body: JSON.stringify({ months, renew }) })) as { url: string };
  return out.url;
}

export interface PaymentState { status: 'pending' | 'succeeded' | 'canceled'; months: number; amount: number; until: string | null; reason: string | null }
export const paymentState = async (id: string): Promise<PaymentState> =>
  await unwrap(await api(`/billing/payment?id=${encodeURIComponent(id)}`)) as PaymentState;

const post = async (path: string, body: unknown = {}) => {
  const out = await unwrap(await api(path, { method: 'POST', body: JSON.stringify(body) }));
  accessChanged();
  return out;
};

/** «1 495 ₽» из копеек. */
export const rub = (kop: number): string => `${Math.round(kop / 100).toLocaleString('ru-RU').replace(/ /g, ' ')} ₽`;
const SROK: Record<number, string> = { 1: '1 месяц', 3: '3 месяца', 6: '6 месяцев', 12: '12 месяцев' };
const day = (iso: string | null) => (iso ? dayLabel(iso.slice(0, 10)) : '');
const DAY = 86_400_000;

function Meter({ name, m }: { name: string; m: { left: number; limit: number } }) {
  const pct = m.limit > 0 ? Math.round((100 * (m.limit - m.left)) / m.limit) : 0;
  return (
    <div className="pw-m">
      <div className="pw-mt"><span>{name}</span><b className="num">осталось {m.left} из {m.limit}</b></div>
      <div className="pw-mtrack"><i style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

type View = 'main' | 'off' | 'refund' | 'plan' | 'history' | 'card';

export function SubscriptionSheet({ onClose, onPay }: { onClose: () => void; onPay: () => void }) {
  const access = useAccess();
  const [bill, setBill] = useState<Billing | null>(null);
  const [view, setView] = useState<View>('main');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const load = () => fetchBilling().then(setBill).catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  useEffect(() => { void load(); }, []);

  const run = async (fn: () => Promise<unknown>, after?: () => void) => {
    setBusy(true); setErr(null);
    try { await fn(); await load(); after?.(); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };

  const sub = bill?.subscription ?? null;
  const back = () => { setView('main'); setErr(null); };

  if (view === 'off' && sub) {
    return (
      <SheetWrap title="Отключить автопродление?" onClose={onClose} onBack={back} backLabel="Назад к подписке">
        <div className="pw-plain">
          <p>Pro останется до <b>{day(sub.endsAt)}</b>. Больше ничего не спишется.</p>
          <p>Потом — Free: записи, цели, платежи и вся история остаются. Голосом, чеки и вопросы SKOKA закроются.</p>
        </div>
        {err && <div className="note warn" role="alert">{err}</div>}
        <div className="form">
          <button className="btn" disabled={busy} onClick={() => void run(() => post('/billing/autorenew', { on: false }), back)}>Отключить</button>
          <button className="btn pri" onClick={back}>Оставить автопродление</button>
        </div>
      </SheetWrap>
    );
  }

  if (view === 'refund' && bill) {
    const r = bill.refund;
    if (done) {
      return (
        <SheetWrap title="Вернём деньги" onClose={onClose}>
          <div className="pw-top refund"><span>Вернём</span><b className="num">{done}</b></div>
          <div className="pw-plain"><p>Pro выключен, все записи на месте. Деньги придут на ту же карту — обычно за 1–10 дней, это зависит от банка. Письмо с суммой — на почте.</p></div>
          <div className="form"><button className="btn pri" onClick={onClose}>Понятно</button></div>
        </SheetWrap>
      );
    }
    const usedDays = r ? Math.max(0, Math.ceil((Date.now() - new Date(r.periodStart).getTime()) / DAY)) : 0;
    const totalDays = r ? Math.round((new Date(r.periodEnd).getTime() - new Date(r.periodStart).getTime()) / DAY) : 0;
    return (
      <SheetWrap title="Вернуть деньги" onClose={onClose} onBack={back} backLabel="Назад к подписке">
        {!r ? (
          <div className="pw-plain"><p>Возвращать нечего: оплаченный срок закончился или деньги уже вернули.</p>
            <p>Списали дважды или Pro не открылся — напиши на support@skokadeneg.ru, вернём всё.</p></div>
        ) : (
          <>
            <div className="oprows pw-q">
              <div className="oprow"><span>Оплата {day(r.paidAt)}</span><span className="opval plain">{rub(r.paid)} · {SROK[r.months]}</span></div>
              <div className="oprow"><span>Прошло</span><span className="opval plain">{usedDays} из {totalDays} дн.</span></div>
            </div>
            <div className="pw-top refund"><span>Вернём</span><b className="num">{rub(r.amount)}</b></div>
            <div className="pw-plain"><p>Pro закончится сразу. Деньги придут на {r.method ?? 'ту же карту'} — обычно за 1–10 дней, это зависит от банка.</p></div>
            <div className="pick"><div className="k">Что не так? Необязательно</div>
              <div className="chips">
                {['Дорого', 'Не пользуюсь', 'Чего-то не хватает', 'Что-то не работает'].map((x) => (
                  <button key={x} className={`chip${reason === x ? ' on' : ''}`} onClick={() => setReason(reason === x ? null : x)}>{x}</button>
                ))}
              </div></div>
            {err && <div className="note warn" role="alert">{err}</div>}
            <div className="form">
              <button className="btn danger" disabled={busy}
                onClick={() => void run(async () => {
                  const out = await post('/billing/refund', { reason }) as { amount: number };
                  setDone(rub(out.amount));
                })}>Вернуть {rub(r.amount)}</button>
              <div className="pw-under">Списали дважды или Pro не открылся — вернём всё: support@skokadeneg.ru</div>
            </div>
          </>
        )}
      </SheetWrap>
    );
  }

  if (view === 'plan' && sub) {
    return (
      <SheetWrap title="Срок продления" onClose={onClose} onBack={back} backLabel="Назад к подписке">
        <div className="pw-plain"><p>Со следующего продления, {day(sub.endsAt)}. Оплаченный срок не меняется.</p></div>
        <div className="oprows pw-q">
          {([[1, 39_900], [3, 99_000], [6, 179_000], [12, 299_000]] as const).map(([m, kop]) => (
            <button key={m} className="oprow" disabled={busy} onClick={() => void run(() => post('/billing/plan', { months: m }), back)}>
              <span>{SROK[m]}{sub.months === m ? ' · сейчас' : ''}</span>
              <span className="opval plain">{rub(kop)}{m > 1 ? ` · ${rub(Math.round(kop / m / 100) * 100)} в мес` : ''}</span>
            </button>
          ))}
        </div>
        {err && <div className="note warn" role="alert">{err}</div>}
      </SheetWrap>
    );
  }

  if (view === 'card' && sub) {
    return (
      <SheetWrap title="Карта для продления" onClose={onClose} onBack={back} backLabel="Назад к подписке">
        <div className="pw-plain">
          <p>Сейчас продлевается с <b>{sub.method ?? 'сохранённой карты'}</b>.</p>
          <p>Новая карта сохраняется при оплате: оплати следующий срок новой картой с включённым продлением — дальше Pro будет продлеваться с неё. Оплаченные дни не пропадут: новый срок добавится к нынешнему.</p>
          <p>Или отвяжи карту совсем — автопродление выключится, Pro доработает до {day(sub.endsAt)}.</p>
        </div>
        {err && <div className="note warn" role="alert">{err}</div>}
        <div className="form">
          <button className="btn pri" onClick={onPay}>Оплатить новой картой</button>
          <button className="btn" disabled={busy} onClick={() => void run(() => post('/billing/forget-card'), back)}>Отвязать карту</button>
        </div>
      </SheetWrap>
    );
  }

  if (view === 'history' && bill) {
    return (
      <SheetWrap title="Оплаты и чеки" onClose={onClose} onBack={back} backLabel="Назад к подписке">
        {bill.payments.length === 0 && <div className="note">Оплат ещё не было.</div>}
        <section className="pw-links">
          {bill.payments.map((p) => (
            <div key={p.id} className="li">
              <div className="nm"><b>{p.kind === 'renewal' ? 'Продление' : 'Оплата'} · {SROK[p.months]}</b>
                <span>{day(p.at)}{p.method ? ` · ${p.method}` : ''}{p.status === 'canceled' ? ' · не прошла' : ''}
                  {p.refunded ? ` · возврат ${rub(p.refunded)}` : ''}</span></div>
              <div className="am num">{rub(p.amount)}</div>
            </div>
          ))}
        </section>
        <div className="pw-under">Кассовые чеки приходят на почту от ЮKassa после каждой оплаты и возврата.</div>
      </SheetWrap>
    );
  }

  return (
    <SheetWrap title="Подписка" onClose={onClose}>
      {!bill || !access ? <div className="skel" /> : (
        <SubscriptionMain access={access} bill={bill} busy={busy} err={err} onPay={onPay}
          onView={setView} onRenew={() => void run(() => post('/billing/autorenew', { on: true }))}
          onForget={() => void run(() => post('/billing/forget-card'))} />
      )}
    </SheetWrap>
  );
}

function SubscriptionMain({ access, bill, busy, err, onPay, onView, onRenew, onForget }: {
  access: AccessState; bill: Billing; busy: boolean; err: string | null; onPay: () => void;
  onView: (v: View) => void; onRenew: () => void; onForget: () => void;
}) {
  const sub = bill.subscription;
  const pro = access.tier === 'pro';
  // Продление не прошло, а Pro ещё работает льготные дни — и пока пробуем, и
  // после окончательного отказа банка: человеку надо успеть оплатить вручную.
  const failed = Boolean(sub?.graceUntil && new Date(sub.graceUntil) > new Date()
    && (!sub.endsAt || new Date(sub.endsAt) < new Date()));
  return (
    <>
      {failed && sub && (
        <section className="blk alarm">
          <div className="pw-st"><b>Не получилось продлить</b>
            <span>Банк не пропустил оплату {rub(sub.nextAmount ?? 0)} с {sub.method ?? 'карты'}.
              {sub.renews ? ' Пробуем ещё раз раз в сутки.' : ' Больше пробовать не будем — оплати сам, если Pro нужен.'}
              {' '}Pro работает до {day(sub.graceUntil)}.</span></div>
          <button className="btn pri" onClick={onPay}>Оплатить сейчас</button>
          {sub.renews && <button className="btn" onClick={() => onView('card')}>Другая карта</button>}
        </section>
      )}
      <div className="pro-pass">
        <span className="pp-l"><ProLockup />
          <b>{pro ? `до ${day(failed ? sub!.graceUntil : sub?.endsAt ?? access.endsAt)}` : access.tier === 'trial' ? 'Пробный период' : 'Free'}</b>
          <span>{sub?.months ? `${SROK[sub.months]} · ${rub(sub.nextAmount ?? 0)}` : pro ? 'подключён' : 'голосом, чеки, выписки, вопросы SKOKA'}</span></span>
      </div>
      {sub?.method && (
        <div className="oprows pw-q">
          <button className="oprow" onClick={() => onView('card')}><span>Карта</span><span className="opval">{sub.method}</span></button>
        </div>
      )}
      {pro && sub?.paid && (
        <div className="form">
          <label className="li sw">
            <div className="nm"><b>Продлевать автоматически</b>
              <span>{sub.renews
                ? `${rub(sub.nextAmount ?? 0)} спишется ${day(sub.endsAt)}, напомним за ${sub.months === 12 ? '7 и 3 дня' : '3 дня'}`
                : `Выключено: после ${day(sub.endsAt)} — Free, списаний не будет`}</span></div>
            <input type="checkbox" checked={sub.renews} disabled={busy || (!sub.renews && !sub.cardSaved)}
              onChange={(e) => (e.target.checked ? onRenew() : onView('off'))} />
            <span className="swbox" aria-hidden="true" />
          </label>
          {!sub.renews && !sub.cardSaved && <div className="pw-under">Чтобы Pro продлевался сам, оплати следующий срок с включённым продлением.</div>}
        </div>
      )}
      {err && <div className="note warn" role="alert">{err}</div>}
      {(pro || access.tier === 'trial') && (
        <>
          <div className="pw-sec">{access.resetsOn ? `До ${dayLabel(access.resetsOn)}` : 'Пробный период'}</div>
          <Meter name="Голосом и словами" m={access.phrase} />
          <Meter name="Чеки и выписки" m={access.receipt} />
          <Meter name="Вопросы SKOKA" m={access.analysis} />
          {access.resetsOn && <div className="pw-mw">обновятся {dayLabel(access.resetsOn)}, в день оплаты</div>}
        </>
      )}
      <section className="pw-links">
        {pro && sub?.paid && sub.renews && (
          <button className="li w" onClick={() => onView('plan')}>
            <div className="nm"><b>Сменить срок</b><span>3, 6 или 12 месяцев — со следующего продления</span></div>
            <span className="ch"><Icon name="chevron-right" size={13} /></span></button>
        )}
        <button className="li w" onClick={() => onView('history')}>
          <div className="nm"><b>Оплаты и чеки</b><span>{bill.payments.length ? `${bill.payments.length} · последняя ${day(bill.payments[0]!.at)}` : 'оплат ещё не было'}</span></div>
          <span className="ch"><Icon name="chevron-right" size={13} /></span></button>
        {bill.refund && (
          <button className="li w" onClick={() => onView('refund')}>
            <div className="nm"><b>Вернуть деньги</b><span>за неиспользованные дни — {rub(bill.refund.amount)}</span></div>
            <span className="ch"><Icon name="chevron-right" size={13} /></span></button>
        )}
        {!pro && (
          <button className="li w" onClick={onPay}>
            <div className="nm"><b>Подключить Pro</b><span>{access.earlyYear ? `новым пользователям — ${access.earlyYear.toLocaleString('ru-RU')} ₽ за первый год` : 'от 249 ₽ в месяц'}</span></div>
            <span className="ch"><Icon name="chevron-right" size={13} /></span></button>
        )}
      </section>
      {sub?.cardSaved && (
        <button className="textact" disabled={busy} onClick={onForget}>Отвязать карту</button>
      )}
    </>
  );
}
